/**
 * Composition root — the ONLY file that imports adapters.
 *
 * Every port has multiple adapters; PORTS_CONFIG picks one. If a UI
 * component, hook, engine or domain module ever imports something from
 * `/adapters/*`, that is an architecture violation and must be moved here.
 */
import { PORTS_CONFIG } from "./ports-config";
import { isCommercialContext } from "../config/environment";

// ---- STORAGE ----
import { createMemoryStorage } from "../adapters/storage/memory";
import { createHttpStorage } from "../adapters/storage/http";
import { createMongoStorage } from "../adapters/storage/mongodb";
import { createPostgresStorage } from "../adapters/storage/postgres";
import { createSupabaseStorage } from "../adapters/storage/supabase";

// ---- CRM ----
import { createMockCRM } from "../adapters/crm/mock";
import { createNoneCRM } from "../adapters/crm/none";
import { createGhlCRM } from "../adapters/crm/ghl";
import { createHubspotCRM } from "../adapters/crm/hubspot";
import { createSalesforceCRM } from "../adapters/crm/salesforce";

// ---- MESSAGING ----
import { createMockMessaging } from "../adapters/messaging/mock";
import { createNoneMessaging } from "../adapters/messaging/none";
import { createWhatsappMessaging } from "../adapters/messaging/whatsapp";
import { createSmsMessaging } from "../adapters/messaging/sms";
import { createEmailMessaging } from "../adapters/messaging/email";

// ---- LLM ----
import { createTemplatesLLM } from "../adapters/llm/templates";
import { createClaudeLLM } from "../adapters/llm/claude";
import { createGeminiLLM } from "../adapters/llm/gemini";
import { createOpenAILLM } from "../adapters/llm/openai";

// ---- INTELLIGENCE ----
import { createHeuristicIntelligence } from "../adapters/intelligence/heuristic";

// ---- KNOWLEDGE ----
import { createMockKnowledge } from "../adapters/knowledge/mock";
import { createNoneKnowledge } from "../adapters/knowledge/none";
import { createMcpKnowledge } from "../adapters/knowledge/mcp";

// ---- CALENDAR ----
import { createMockCalendar } from "../adapters/calendar/mock";
import { createNoneCalendar } from "../adapters/calendar/none";
import { createGoogleCalendar } from "../adapters/calendar/google";
import { createOutlookCalendar } from "../adapters/calendar/outlook";
import { createCalendlyCalendar } from "../adapters/calendar/calendly";

// ---- ERP ----
import { createMockERP } from "../adapters/erp/mock";
import { createNoneERP } from "../adapters/erp/none";
import { createOdooERP } from "../adapters/erp/odoo";
import { createDynamicsERP } from "../adapters/erp/dynamics";
import { createSapERP } from "../adapters/erp/sap";

// ---- Domain (single source of upward exposure) ----
import { createRecoveryEngine } from "../domain/engines/recovery";
import { createRenewalEngine } from "../domain/engines/renewal";
import { createCrossSellEngine as createCrossSellEngineReal } from "../domain/engines/crossSell";
import {
  createCrossSellEngine as createCrossSellEngineStub, createUpsellEngine,
  createRetentionEngine, createReferralEngine, createProspectingEngine,
  makeStubEngine,
} from "../domain/engines/stubs";
import { createPortfolio } from "../domain/portfolio";

// Venta Cruzada (Sprint 12): activación EXCLUSIVA por variable de entorno,
// nunca automática. Cada despliegue (laboratorio/Abel/Guillermo) es su
// propio build de React con sus propias REACT_APP_* — así que esto NO es
// una bandera en memoria compartida, es una decisión tomada en tiempo de
// compilación por despliegue. Fijar REACT_APP_CROSS_SELL_ENABLED=true SOLO
// en el .env del laboratorio; nunca en el de Abel/Guillermo hasta que se
// decida deliberadamente activarlo para ellos.
const CROSS_SELL_ENABLED = process.env.REACT_APP_CROSS_SELL_ENABLED === "true";

// Recovery: activo por default (motor de referencia, ya probado en
// laboratorio) — SALVO que un despliegue lo apague explícitamente. Existe
// para el caso "duda real sobre Recuperación" (checklist de entrega del
// Socio Fundador): si la recepción automática de respuestas no está
// confirmada para ESE despliegue, se apaga con una sola variable, sin
// tocar domain/engines/recovery.js.
const RECOVERY_ENABLED = process.env.REACT_APP_RECOVERY_ENABLED !== "false";
const createRecoveryEngineStub = makeStubEngine("recovery", {
  label: "Recovery",
  question: "¿Dónde está el dinero que perdí y puedo recuperar?",
  status: "coming_soon",
});

// ---- Adapter registries ----
// Exported (read-only use) so container.conformance.test.js can verify every
// registered adapter implements its port's full method surface, without a
// second, duplicated import list living in the test file.
export const REGISTRIES = {
  storage: {
    memory: createMemoryStorage, http: createHttpStorage,
    mongodb: createMongoStorage,
    postgres: createPostgresStorage, supabase: createSupabaseStorage,
  },
  crm: {
    mock: createMockCRM, none: createNoneCRM, ghl: createGhlCRM,
    hubspot: createHubspotCRM, salesforce: createSalesforceCRM,
  },
  messaging: {
    mock: createMockMessaging, none: createNoneMessaging,
    whatsapp: createWhatsappMessaging, sms: createSmsMessaging, email: createEmailMessaging,
  },
  llm: {
    templates: createTemplatesLLM, claude: createClaudeLLM,
    gemini: createGeminiLLM, openai: createOpenAILLM,
  },
  knowledge: {
    mock: createMockKnowledge, none: createNoneKnowledge, mcp: createMcpKnowledge,
  },
  calendar: {
    mock: createMockCalendar, none: createNoneCalendar,
    google: createGoogleCalendar, outlook: createOutlookCalendar, calendly: createCalendlyCalendar,
  },
  erp: {
    mock: createMockERP, none: createNoneERP,
    odoo: createOdooERP, dynamics: createDynamicsERP, sap: createSapERP,
  },
};

function resolve(port) {
  const key = PORTS_CONFIG[port];
  const factory = REGISTRIES[port]?.[key];
  if (!factory) throw new Error(`No adapter '${key}' registered for port '${port}'`);
  // Regla de seguridad explícita (Sprint 11, Parte B): un mock nunca debe
  // quedar activo en beta_founder/production — falla ruidoso en vez de
  // silencioso, en vez de confiar en que alguien recuerde el env var.
  if (port === "messaging" && key === "mock" && isCommercialContext()) {
    throw new Error(
      "REACT_APP_MESSAGING_ADAPTER=mock no está permitido con " +
      "REACT_APP_EXECUTION_MODE=beta_founder/production. Usa 'whatsapp' " +
      "(o 'none') y configura el backend."
    );
  }
  return factory();
}

// ---- Compose ----
const storage = resolve("storage");
const crm = resolve("crm");
const messaging = resolve("messaging");
const llm = resolve("llm");
const knowledge = resolve("knowledge");
const calendar = resolve("calendar");
const erp = resolve("erp");
const intelligence = createHeuristicIntelligence();

const deps = { storage, crm, messaging, llm, knowledge, calendar, erp, intelligence };

const engines = {
  recovery: (RECOVERY_ENABLED ? createRecoveryEngine : createRecoveryEngineStub)(deps),
  renewal: createRenewalEngine(deps),
  cross_sell: (CROSS_SELL_ENABLED ? createCrossSellEngineReal : createCrossSellEngineStub)(deps),
  upsell: createUpsellEngine(deps),
  retention: createRetentionEngine(deps),
  referral: createReferralEngine(deps),
  prospecting: createProspectingEngine(deps),
};

const portfolio = createPortfolio({ engines, storage, crm });

export const container = {
  ports: { storage, crm, messaging, llm, knowledge, calendar, erp, intelligence },
  engines,
  portfolio,
  config: PORTS_CONFIG,
};
